import { FC, ReactNode, useContext } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { PdpSharedShippingInfo } from './PdpSharedShippingInfo';
import { PdpSharedShippingInfoRedesign } from './PdpSharedShippingInfoRedesign';
import { RelayVariables, Store } from '../../global/relayVariableTypes';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import { PdpSharedShippingInfoWrapper_item$key } from './__generated__/PdpSharedShippingInfoWrapper_item.graphql';
import { PdpSharedShippingInfoWrapper_user$key } from './__generated__/PdpSharedShippingInfoWrapper_user.graphql';

type Props = {
    item: PdpSharedShippingInfoWrapper_item$key;
    user: PdpSharedShippingInfoWrapper_user$key | null;
    currency: string;
    children?: ReactNode;
    leadTime?: string | null;
    renderTooltip?: boolean;
    isClient?: boolean;
    title?: ReactNode;
    displayInline?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    itemQuantity?: number | null;
    calculateLowerShippingInfo?: boolean;
    positioning?: 'sidebar' | 'seller_details';
};

const PdpSharedShippingInfoWrapper: FC<Props> = ({
    item: itemRef,
    user: userRef,
    currency,
    children,
    leadTime,
    renderTooltip,
    isClient = false,
    title,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    itemQuantity,
    calculateLowerShippingInfo = false,
    positioning,
}) => {
    const user = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_user on User {
                serviceId
                isVerifiedTrade
            }
        `,
        userRef
    );
    const item = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_item on Item {
                serviceId
            }
        `,
        itemRef
    );

    const { isMobile } = useContext(ServerVarsContext);

    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );

    const { isSiderailRedesignVariant } = relayVariables;

    if (isSiderailRedesignVariant && !isMobile && positioning === 'sidebar') {
        return (
            <PdpSharedShippingInfoRedesign
                currency={currency}
                itemId={item.serviceId || ''}
                userId={user?.serviceId || ''}
                isTrade={user?.isVerifiedTrade || false}
                loadingTitle={title}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                isClient={isClient}
                itemQuantity={itemQuantity}
                calculateLowerShippingInfo={calculateLowerShippingInfo}
                positioning={positioning}
            >
                {children}
            </PdpSharedShippingInfoRedesign>
        );
    } else {
        return (
            <PdpSharedShippingInfo
                renderTooltip={renderTooltip}
                currency={currency}
                leadTime={leadTime || ''}
                itemId={item.serviceId || ''}
                userId={user?.serviceId || ''}
                isTrade={user?.isVerifiedTrade || false}
                title={title}
                displayInline={displayInline}
                preLinkQuoteDetails={preLinkQuoteDetails}
                postLinkQuoteDetails={postLinkQuoteDetails}
                postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                isClient={isClient}
                itemQuantity={itemQuantity}
                calculateLowerShippingInfo={calculateLowerShippingInfo}
                positioning={positioning}
            >
                {children}
            </PdpSharedShippingInfo>
        );
    }
};

export default PdpSharedShippingInfoWrapper;
